import React, { useState, useRef, useEffect } from 'react';
import { Calendar, DateObject } from 'react-multi-date-picker';
import colors from 'react-multi-date-picker/plugins/colors';
import {
  getLeaveTypeForUser,
  getUserData,
  getUserLeaves,
} from '../../../services/UserService';
import KeycloakService from '../../../services/KeycloakService';
import { getUserWFH } from '../../../services/WFHService';
import {
  CalendarViewAttribute,
  SHOW_NOTIFICATION,
  colorOptionsLeaveType,
  holidayCalenderHighlightColor,
  wfhCalenderHighlightColor,
} from '../../../constants';
import { useDispatch, useSelector } from 'react-redux';
import GenericLoader from '../../../components/Loader/GenericLoader';
import { Tooltip, Grid, Box, Typography, Stack, Chip } from '@mui/material';
import './calendarView.css';
import { getHolidays } from '../../../admin/Services/MasterdataService';

const CalendarView = () => {
  const currentUserId = () => KeycloakService.getUserDetail();
  const people = useSelector((state) => state.peopleState);
  const dispatch = useDispatch();
  const calendarRef = useRef();
  const [loader, setLoader] = useState(false);
  const [showError, setShowError] = useState(false);
  const [leavesandWFHList, setLeavesandWFHList] = useState([]);
  const [tooltipProps, setTooltipProps] = useState({
    xCoordinate: 0,
    yCoordinate: 0,
    title: '',
  });
  const [labels, setLabels] = useState([]);
  const [labelsFlag, setLabelsFlag] = useState(false);
  const [leaveDataLoadFlag, setLeaveDataLoadFlag] = useState(false);
  const [wfhDataLoadFlag, setWfhDataLoadFlag] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [numberOfItemPerRow, setNumberOfItemPerRow] = useState(0);

  const date = new Date();
  const [open, setOpen] = React.useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  const dipatchNotification = (typ, msg) => {
    dispatch({
      type: SHOW_NOTIFICATION,
      data: {
        open: true,
        type: typ,
        message: msg,
      },
    });
  };

  const getLeaveTypesLabels = async () => {
    const response = await getLeaveTypeForUser(date.getFullYear());
    let leavesLabel = [];
    if (response.isError === false) {
      setLabelsFlag(true);
      response.data.map((data) => {
        leavesLabel.push({
          id: data.id,
          label: data.leaveName,
          color: data.color,
        });
      });
      setLabels([
        {
          id: 1000,
          label: 'WFH',
          color: wfhCalenderHighlightColor,
        },
        {
          id: 2000,
          label: 'holiday',
          color: holidayCalenderHighlightColor,
        },
        ...leavesLabel,
      ]);
    } else {
      setLabels([
        {
          id: 1000,
          label: 'WFH',
          color: wfhCalenderHighlightColor,
        },
      ]);
    }
    if (response.status !== 200) {
      setLabelsFlag(false);
      dipatchNotification(response.type, response.message);
    }
  };

  const getLeaveandWFHList = async () => {
    let userLeaves;
    let userWFH;
    let employeeId;
    let filteredData;

    if (people.id === '' && currentUserId !== undefined) {
      employeeId = currentUserId();
    } else {
      employeeId = people.id;
    }

    userLeaves = await getUserLeaves(employeeId, {
      status: 'APPROVED',
    });
    if (userLeaves.status !== 200) {
      setLeaveDataLoadFlag(false);
      dipatchNotification(userLeaves.type, userLeaves.message);
      return;
    }

    userWFH = await getUserWFH(employeeId, 'WFH', 0, 0, {});
    if (userWFH.status !== 200) {
      setWfhDataLoadFlag(false);
      dipatchNotification(userWFH.type, userWFH.message);
      return;
    } else {
      filteredData = userWFH.data.data.content.filter(
        (item) =>
          item.status === 'APPROVED' ||
          item.status === 'TASK_PENDING' ||
          item.status === 'TASK_APPROVED',
      );
    }

    // Fetch holiday list
    const user = await getUserData(employeeId);
    const currentYear = new Date().getFullYear();
    const startDate = `${currentYear}-01-01`;
    const endDate = `${currentYear}-12-31`;

    let holidays = [];
    if (user.data.locationId) {
      const holidayResponse = await getHolidays(
        user?.data?.locationId,
        startDate,
        endDate,
      );

      holidays =
        holidayResponse.data && Array.isArray(holidayResponse.data)
          ? holidayResponse.data.map((holiday) => holiday.date)
          : [];
    }

    const excludeHolidays = (startDate, endDate, holidays) => {
      const result = [];
      const start = new Date(startDate);
      const end = new Date(endDate);

      while (start <= end) {
        const formattedDate = start.toISOString().split('T')[0];
        if (Array.isArray(holidays) && !holidays.includes(formattedDate)) {
          result.push(new Date(start));
        }
        start.setDate(start.getDate() + 1);
      }

      return result;
    };

    if (userWFH.isError === false && userLeaves.isError === false) {
      setLeaveDataLoadFlag(true);
      setWfhDataLoadFlag(true);

      const excludeWeekends = (startDate, endDate) => {
        const result = [];
        const start = new Date(startDate);
        const end = new Date(endDate);

        while (start <= end) {
          const day = start.getDay();
          if (day !== 0 && day !== 6) {
            result.push(new Date(start));
          }
          start.setDate(start.getDate() + 1);
        }

        return result;
      };

      const temp = [];
      temp.push(
        ...userLeaves.data.data.content.flatMap(
          ({ type, leaveId, startDate, endDate }) =>
            excludeWeekends(startDate, endDate)
              .flatMap((date) =>
                excludeHolidays(
                  date.toISOString().split('T')[0],
                  date.toISOString().split('T')[0],
                  holidays,
                ),
              )
              .map((date) => ({
                type,
                leaveId,
                startDate: date.toISOString().split('T')[0],
                endDate: date.toISOString().split('T')[0],
              })),
        ),
      );
      temp.push(
        ...filteredData.map(({ type, startDate, endDate }) => ({
          type,
          startDate,
          endDate,
        })),
      );
      temp.push(
        ...holidays.map((date) => ({
          type: 'holiday',
          startDate: date,
          endDate: date,
        })),
      );

      setLeavesandWFHList(temp);
      setLoader(false);
    }
  };

  // const holidayList = async () => {
  //   let employeeId;
  //   if (people.id === '' && currentUserId !== undefined) {
  //     employeeId = currentUserId();
  //   } else {
  //     employeeId = people.id;
  //   }
  //   const user = await getUserData(employeeId);
  //   const currentYear = new Date().getFullYear();

  //   const startDate = `${currentYear}-01-01`;
  //   const endDate = `${currentYear}-12-31`;

  //   const response = await getHolidays(
  //     user?.data?.locationId,
  //     startDate,
  //     endDate,
  //   );
  // };

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (screenWidth > 1300) {
      setNumberOfItemPerRow(4);
    } else if (screenWidth > 1050) {
      setNumberOfItemPerRow(3);
    } else if (screenWidth > 800) {
      setNumberOfItemPerRow(2);
    } else {
      setNumberOfItemPerRow(1);
    }
  }, [screenWidth]);

  useEffect(() => {
    calendarEhnacement(numberOfItemPerRow);
  }, [numberOfItemPerRow]);

  useEffect(() => {
    getLeaveandWFHList();
    getLeaveTypesLabels();
  }, [people]);

  useEffect(() => {
    if (labelsFlag && leaveDataLoadFlag && wfhDataLoadFlag) {
      convertLeavelistToArrOfDates();
    }
    // holidayList();
  }, [
    leavesandWFHList,
    labels,
    labelsFlag,
    leaveDataLoadFlag,
    wfhDataLoadFlag,
  ]);

  const getColorNameFromLeaveTypeColor = (color) => {
    for (let i = 0; i < colorOptionsLeaveType.length; i++) {
      const element = colorOptionsLeaveType[i];
      if (element.color === color) {
        return element.label.toLowerCase();
      }
    }
  };

  const getColorCode = (name) => {
    if (name === 'wfhcolor') return wfhCalenderHighlightColor;

    if (name === 'holidaycolor') {
      return holidayCalenderHighlightColor;
    }
    for (let i = 0; i < colorOptionsLeaveType.length; i++) {
      const col = colorOptionsLeaveType[i];
      if (col.label.toLowerCase() === name) return col.color;
    }
  };

  const getLabel = (color) => {
    for (let i = 0; i < labels.length; i++) {
      const col = labels[i];
      if (col.color === color) return col.label;
    }
  };

  const getColorCodeFromLeaveId = (id) => {
    for (let i = 0; i < labels.length; i++) {
      const label = labels[i];
      if (label.id === id) return label.color;
    }
  };

  const convertLeavelistToArrOfDates = () => {
    const updatedArrOfDates = [];
    leavesandWFHList.map((leave, index) => {
      var temp = new DateObject(leave.startDate);
      while (temp <= new DateObject(leave.endDate)) {
        if (leave.type === 'WFH') {
          temp.color = 'wfhcolor';
        } else if (leave.type === 'holiday') {
          temp.color = 'holidaycolor';
        } else {
          temp.color = getColorNameFromLeaveTypeColor(
            getColorCodeFromLeaveId(leave.leaveId),
          );
        }
        updatedArrOfDates.push(temp);
        temp = new DateObject(temp).add(1, 'day');
      }
    });
    setProps({ ...props, value: updatedArrOfDates });
  };

  const calendarEhnacement = (itemCount) => {
    const calenderColors =
      calendarRef?.current?.querySelectorAll('.rmdp-colors');
    const calenderDayPicker =
      calendarRef?.current?.querySelectorAll('.rmdp-day-picker');
    if (
      calenderDayPicker &&
      calenderDayPicker[0] &&
      calenderDayPicker[0].children
    ) {
      let monthBoxList = calenderDayPicker[0].children;
      calenderDayPicker[0].style.gridTemplateColumns = Array(itemCount)
        .fill('1fr')
        .join(' ');
      for (let i = 0; i < monthBoxList.length; i++) {
        if (i < itemCount * (12 / itemCount - 1)) {
          if (i % itemCount < itemCount - 1) {
            monthBoxList[i].style.borderColor = 'black';
            monthBoxList[i].style.borderStyle = 'solid';
            monthBoxList[i].style.borderWidth = '1px 0px 0px 1px';
            monthBoxList[i].style.marginRight = '0px';
          } else {
            monthBoxList[i].style.borderColor = 'black';
            monthBoxList[i].style.borderStyle = 'solid';
            monthBoxList[i].style.borderWidth = '1px 1px 0px 1px';
            monthBoxList[i].style.marginRight = '0px';
          }
        } else {
          if (i % itemCount < itemCount - 1) {
            monthBoxList[i].style.borderColor = 'black';
            monthBoxList[i].style.borderStyle = 'solid';
            monthBoxList[i].style.borderWidth = '1px 0px 1px 1px';
            monthBoxList[i].style.marginRight = '0px';
          } else {
            monthBoxList[i].style.borderColor = 'black';
            monthBoxList[i].style.borderStyle = 'solid';
            monthBoxList[i].style.borderWidth = '1px 1px 1px 1px';
            monthBoxList[i].style.marginRight = '0px';
          }
        }
      }
    }

    if (calenderColors && calenderColors[0] && calenderColors[0].style) {
      calenderColors[0].style.display = 'none';
    }
  };

  const addEventListenersToDates = () => {
    const calendarCells = calendarRef?.current?.querySelectorAll('.rmdp-day');

    if (calendarCells) {
      calendarCells.forEach((cell) => {
        cell.addEventListener('mouseenter', handleDateHover);
      });
    }
    calendarEhnacement(numberOfItemPerRow);
  };

  const handleDateHover = (event) => {
    const colorString = event.target.children[0]?.classList[2];
    if (colorString) {
      setTooltipProps({
        xCoordinate: event?.pageX,
        yCoordinate: event?.pageY - 20,
        title: getLabel(getColorCode(colorString.split('-').pop())),
      });
      handleTooltipOpen();
    } else {
      handleTooltipClose();
    }
  };

  const [props, setProps] = useState({
    value: [],
    plugins: [colors({ colors: ['blue', 'red', 'green', 'yellow', 'orange'] })],
  });

  return (
    <>
      {loader ? (
        <div style={{ height: '70vh', overflow: 'hidden' }}>
          <GenericLoader
            rectAttributes={CalendarViewAttribute}
            showError={showError}
            setShowError={setShowError}
            width={1500}
            height={2500}
          />
        </div>
      ) : (
        <>
          <Stack
            style={{
              marginBottom: '5px',
              alignItems: 'center',
              justifyContent: 'space-around',
            }}
            direction="row"
            spacing={1}
          >
            {labels.map((data, index) => (
              <Chip
                size="small"
                style={{ backgroundColor: 'white', color: 'black' }}
                icon={
                  <div
                    style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: data.color,
                    }}
                  ></div>
                }
                label={data.label}
              />
            ))}
          </Stack>
          <Tooltip
            style={{
              position: 'absolute',
              opacity: 0,
              left: `${tooltipProps.xCoordinate}px`,
              top: `${tooltipProps.yCoordinate}px`,
            }}
            PopperProps={{
              disablePortal: true,
            }}
            onClose={handleTooltipClose}
            open={open}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            title={tooltipProps.title}
            placement="bottom-start"
          >
            <p>Tool</p>
          </Tooltip>
          <Box>
            <Calendar
              fullYear
              ref={calendarRef}
              {...props}
              onPropsChange={setProps}
              multiple={false}
              readOnly
              mapDays={() => addEventListenersToDates()}
            />
          </Box>
        </>
      )}
    </>
  );
};

export default CalendarView;
